import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Table, Tabs } from 'antd';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import numeral from 'numeral';
import { ContentCustom, PageHeader } from '../../components';
import { routes, subRoutes } from '../../utils/constants/routes';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';

const { TabPane } = Tabs;

const tabList = ['WARRANTY_SUSPENSION', 'TERMINATED_NON_PAYMENT', 'TERMINATED'];

export const Accounting = () => {
  const { message } = useErrorMessage();
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const activeTab = params.get('t') || 'WARRANTY_SUSPENSION';

  const getContracts = async () => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `contracts?populate=quittances&!previous_contract`
      });
      setContracts(data.map((d) => ({ ...d, key: d._id })));
    } catch (e) {
      message(e);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    (async () => {
      await getContracts();
    })();
    // eslint-disable-next-line
  }, []);

  const handleTabChange = (key) => {
    navigate({
      pathname: location.pathname,
      search: `?t=${key}`
    });
  };

  const getBalanceDue = (quittance) =>
    quittance.filter((q) => q.quittance_status === 'NOT_PAID').reverse()[0];

  const getRefund = (quittance) =>
    quittance.filter((q) => q.refund).reverse()[0];

  return (
    <>
      <PageHeader title={t('accounting.title')} />
      <ContentCustom>
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          {tabList.map((k) => (
            <TabPane key={k} tab={t(`accounting.tabs.${k}`)} />
          ))}
        </Tabs>
        <Table
          pagination={{ hideOnSinglePage: true }}
          loading={isLoading}
          dataSource={contracts.filter((c) => c.status === activeTab)}
          scroll={{ x: 800 }}
          columns={[
            {
              title: t('contracts.column.number'),
              dataIndex: 'number',
              key: 'number',
              render: (number, { key }) => (
                <Link
                  to={`${routes.CONTRACTS}${subRoutes.CONTRACTS.PORTFOLIO_CONTRACTS}/show/${key}`}
                >
                  {number}
                </Link>
              ),
              sorter: true
            },
            ...(activeTab === 'WARRANTY_SUSPENSION'
              ? [
                  {
                    title: t('contracts.column.suspension_date'),
                    key: 'suspension_date',
                    render: (contract) =>
                      contract && moment(contract.termination_date).format('LL')
                  }
                ]
              : [
                  {
                    title: t('contracts.column.termination_date'),
                    dataIndex: 'termination_date',
                    render: (date) => date && moment(date).format('LL')
                  }
                ]),
            {
              title: t('contracts.column.customer'),
              dataIndex: 'customer',
              key: 'customer',
              render: (customer, { key }) =>
                customer && (
                  <Link
                    to={`${routes.CONTRACTS}${subRoutes.CONTRACTS.PORTFOLIO_CLIENTS}/show/${key}`}
                  >
                    {`${customer.first_name} ${customer.last_name}`}
                  </Link>
                )
            },
            {
              title: t('contracts.column.due'),
              dataIndex: 'quittances',
              key: 'quittances',
              render: (q) =>
                getBalanceDue(q) &&
                `${numeral(getBalanceDue(q).selling_price_customer).format(
                  '0,0.00'
                )}€`
            },
            {
              title: t('contracts.column.refund'),
              dataIndex: 'quittances',
              key: 'quittances',
              render: (q) =>
                getRefund(q) &&
                `${numeral(getRefund(q).refund).format('0,0.00')}€`
            },
            {
              key: 'action',
              render: (contract) => (
                <Link
                  to={`${routes.CONTRACTS}${subRoutes.CONTRACTS.PORTFOLIO_CONTRACTS}/show/${contract.key}`}
                >
                  <Button type="primary">Régulariser</Button>
                </Link>
              )
            }
          ]}
        />
      </ContentCustom>
    </>
  );
};
